/**
 * GovernanceSignal — the ONE primary indicator of a tool's governance posture.
 *
 * This replaces the nine-chip "badge soup" that made the most important tools
 * the noisiest. It encodes two independent channels in a single glance:
 *   - governance coverage (the chip color/icon: gap/declared/governed/strong)
 *   - inherent risk        (an optional trailing risk dot, red-is-legitimate)
 *
 * Everything else (per-param x-fields, confirm message, scope details) lives in
 * CdaContractViewer. Critical tools must be the MOST scannable, not the least.
 */

import type { CdaContract, GovernanceState } from './types';
import { deriveGovernanceState, parseRiskLevel } from './types';
import { governanceStateColor, RISK_COLOR, softFill, softBorder } from './colors';
import { CdaIcon } from './icons';
import type { CdaIconName } from './icons';

const React = (globalThis as any).React;
const { useState, useCallback } = React;

export interface GovernanceSignalProps {
  contract: CdaContract;
  /** Operator surfaces can assert a mutation from an authoritative binding. */
  mutatingBinding?: boolean;
  size?: 'sm' | 'md';
  /** Optional click handler — opens the full contract detail. */
  onClick?: () => void;
  /** Hide the trailing inherent-risk dot (e.g. when risk is shown elsewhere). */
  hideRisk?: boolean;
  className?: string;
}

const STATE_ICON: Record<GovernanceState, CdaIconName> = {
  'read-only': 'eye',
  gap: 'shield-alert',
  declared: 'shield',
  governed: 'shield-check',
  'governed-strong': 'lock',
  unknown: 'circle',
};

export function GovernanceSignal({
  contract,
  mutatingBinding,
  size = 'md',
  onClick,
  hideRisk,
  className,
}: GovernanceSignalProps) {
  const [mounted, setMounted] = useState(false);
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const info = deriveGovernanceState(contract, { mutatingBinding });
  const color = governanceStateColor(info.state);
  const icon = STATE_ICON[info.state];
  const fontSize = size === 'sm' ? 11 : 12;
  const iconSize = size === 'sm' ? 13 : 15;

  const risk = parseRiskLevel(contract['x-risk']);
  const showRisk = !hideRisk && !!risk && risk !== 'read' && info.state !== 'read-only';

  const handleKeyDown = useCallback(
    (e: any) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const Tag = onClick ? 'button' : 'span';

  return React.createElement(
    Tag,
    {
      'data-testid': 'cda-governance-signal',
      'data-governance-state': info.state,
      className: className || undefined,
      onClick: onClick || undefined,
      onKeyDown: onClick ? handleKeyDown : undefined,
      tabIndex: onClick ? 0 : undefined,
      role: onClick ? 'button' : 'img',
      'aria-label': `${info.label}. ${info.summary}`,
      title: info.summary,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: size === 'sm' ? '2px 8px' : '3px 10px',
        borderRadius: 'var(--border-radius-sm, 999px)',
        border: `1px solid ${softBorder(color, info.state === 'gap' ? 45 : 30)}`,
        background: softFill(color, info.state === 'gap' ? 16 : 11),
        color,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.4,
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        opacity: mounted ? 1 : 0,
        transition: 'opacity 180ms ease-out',
      },
    },
    React.createElement(CdaIcon, { name: icon, size: iconSize, color }),
    React.createElement('span', null, info.label),
    showRisk &&
      React.createElement('span', {
        'data-testid': 'cda-governance-signal-risk-dot',
        'aria-hidden': 'true',
        title: `Inherent risk: ${risk}`,
        style: {
          width: 7,
          height: 7,
          borderRadius: '50%',
          marginLeft: 1,
          background: RISK_COLOR[risk as keyof typeof RISK_COLOR],
          flexShrink: 0,
        },
      }),
  );
}
